import React from "react";
import Box from "@mui/joy/Box";
import Typography from "@mui/joy/Typography";
import Tabs from "@mui/joy/Tabs";
import TabList from "@mui/joy/TabList";
import Tab, { tabClasses } from "@mui/joy/Tab";
import TabPanel from "@mui/joy/TabPanel";
import { useLocation, useNavigate } from "react-router-dom";
import BidsTable from "./BidsTable";
import { ANALYTICS_TAB_SLUGS, BidsAnalytics } from "./analytics/BidsAnalytics";

const LIST_SLUG = "list";
const ANALYTICS_PREFIX = "analytics";

function normalizeHash(hash: string): string {
    return (hash || "").replace("#", "").toLowerCase();
}

function getIndicesFromHash(hash: string): { topIndex: number; analyticsIndex: number } {
    const slug = normalizeHash(hash);

    if (!slug || slug === LIST_SLUG) {
        return { topIndex: 0, analyticsIndex: 0 };
    }

    if (slug === ANALYTICS_PREFIX || slug.startsWith(`${ANALYTICS_PREFIX}-`)) {
        const analyticsIndex = ANALYTICS_TAB_SLUGS.indexOf(
            slug as (typeof ANALYTICS_TAB_SLUGS)[number]
        );
        return {
            topIndex: 1,
            analyticsIndex: analyticsIndex >= 0 ? analyticsIndex : 0,
        };
    }

    // обратная совместимость: #analytics / #аналитика
    if (slug === "аналитика") {
        return { topIndex: 1, analyticsIndex: 0 };
    }

    return { topIndex: 0, analyticsIndex: 0 };
}

export function Bids() {
    const navigate = useNavigate();
    const location = useLocation();

    const [{ topIndex, analyticsIndex }, setIndices] = React.useState(() =>
        getIndicesFromHash(location.hash)
    );

    React.useEffect(() => {
        const next = getIndicesFromHash(location.hash);
        setIndices((prev) =>
            prev.topIndex === next.topIndex && prev.analyticsIndex === next.analyticsIndex
                ? prev
                : next
        );
    }, [location.hash]);

    const goHash = (slug: string) => {
        const next = `#${slug}`;
        if (normalizeHash(location.hash) !== slug) {
            navigate(
                { pathname: location.pathname, search: location.search, hash: next },
                { replace: false }
            );
        }
    };

    const handleTopTabChange = (_: React.SyntheticEvent | null, value: number | string | null) => {
        const idx = typeof value === "number" ? value : 0;
        if (idx === 1) {
            goHash(ANALYTICS_TAB_SLUGS[analyticsIndex] || ANALYTICS_TAB_SLUGS[0]);
            return;
        }
        goHash(LIST_SLUG);
    };

    const handleAnalyticsTabChange = (idx: number) => {
        goHash(ANALYTICS_TAB_SLUGS[idx] || ANALYTICS_TAB_SLUGS[0]);
    };

    return (
        <React.Fragment>
            <Box
                component="main"
                className="MainContent"
                sx={{
                    px: { xs: 2, md: 6 },
                    pt: {
                        xs: "calc(12px + var(--Header-height))",
                        sm: "calc(12px + var(--Header-height))",
                        md: 3,
                    },
                    pb: { xs: 2, sm: 2, md: 3 },
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    minWidth: 0,
                    height: "100dvh",
                    gap: 1,
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        mb: 1,
                        gap: 1,
                        flexDirection: { xs: "column", sm: "row" },
                        alignItems: { xs: "start", sm: "center" },
                        flexWrap: "wrap",
                        justifyContent: "space-between",
                    }}
                >
                    <Typography level="h2" component="h1">
                        Заявки
                    </Typography>
                </Box>

                <Tabs
                    value={topIndex}
                    onChange={handleTopTabChange}
                    sx={{ bgcolor: "transparent", flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}
                >
                    <TabList
                        tabFlex={1}
                        size="sm"
                        sx={{
                            pl: { xs: 0, md: 4 },
                            justifyContent: "left",
                            [`&& .${tabClasses.root}`]: {
                                fontWeight: "600",
                                flex: "initial",
                                color: "text.tertiary",
                                [`&.${tabClasses.selected}`]: {
                                    bgcolor: "transparent",
                                    color: "text.primary",
                                    "&::after": {
                                        height: "2px",
                                        bgcolor: "primary.500",
                                    },
                                },
                            },
                        }}
                    >
                        <Tab sx={{ borderRadius: "6px 6px 0 0" }} indicatorInset value={0}>
                            Список
                        </Tab>
                        <Tab sx={{ borderRadius: "6px 6px 0 0" }} indicatorInset value={1}>
                            Аналитика заявок
                        </Tab>
                    </TabList>

                    <TabPanel value={0} sx={{ px: 0, flex: 1, minHeight: 0, overflow: "auto" }}>
                        <BidsTable />
                    </TabPanel>
                    <TabPanel value={1} sx={{ px: 0, flex: 1, minHeight: 0, overflow: "auto" }}>
                        <BidsAnalytics
                            isActive={topIndex === 1}
                            currentTabIndex={analyticsIndex}
                            onTabChange={handleAnalyticsTabChange}
                        />
                    </TabPanel>
                </Tabs>
            </Box>
        </React.Fragment>
    );
}
